// What the policy details page shows, read from `GET /policies/:id`. Read
// defensively: a field of the wrong type reads as absent.

export type PolicyStatus = "finalizing" | "issued" | "other";

export interface PolicyView {
  /** The policy's active quote while the family is still working from it;
   * null once the quote has been completed. */
  quoteId: string | null;
  status: PolicyStatus;
  /** The stage as the record names it; shown when the status is `other`. */
  stage: string | null;
  insuredName: string | null;
  planName: string | null;
  carrierName: string | null;
  policyNumber: string | null;
  issueDate: string | null;
  paymentPeriod: string | null;
}

const FINALIZING_STAGES = [
  "Pending Signatures",
  "Pending Submission",
  "Policy Submitted",
];

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

export function policyStatusFor(stage: string | null): PolicyStatus {
  if (stage === "Policy Issued") return "issued";
  if (stage !== null && FINALIZING_STAGES.includes(stage)) return "finalizing";
  return "other";
}

export function parsePolicyView(raw: unknown): PolicyView {
  const row =
    typeof raw === "object" && raw !== null
      ? (raw as Record<string, unknown>)
      : {};
  const stage = text(row.stage);
  return {
    quoteId: text(row.quote_id),
    status: policyStatusFor(stage),
    stage,
    insuredName: text(row.insured_full_name),
    planName: text(row.plan_name),
    carrierName: text(row.carrier_name),
    policyNumber: text(row.policy_number),
    issueDate: text(row.policy_issue_date),
    paymentPeriod: text(row.payment_period),
  };
}

/** What the quote-stage page says. A policy has an insured by the time it has
 * a quote, but a record without a name still gets a page. */
export function quoteStageTitle(view: PolicyView): string {
  return view.insuredName
    ? `Planning for ${view.insuredName}`
    : "Planning ahead";
}

export const QUOTE_STAGE_BODY =
  "Your quote is saved. Come back to it whenever you're ready.";

export const STATUS_PILL: Record<PolicyStatus, string> = {
  finalizing: "Finalizing",
  issued: "Issued",
  other: "In progress",
};

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** "September 1, 2026" for an ISO date, read as a calendar date so no time
 * zone can move it a day. Null when it is not one. */
export function formatIssueDate(value: string | null): string | null {
  const match = value === null ? null : CALENDAR_DATE.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/** The status sentence under the header. */
export function statusMessage(view: PolicyView): string | null {
  if (view.status === "finalizing") {
    return "Our licensed pre-need agent is reviewing your policy and will be in touch.";
  }
  if (view.status === "issued") {
    const issued = formatIssueDate(view.issueDate);
    return issued
      ? `Your policy was issued on ${issued}.`
      : "Your policy is issued.";
  }
  return null;
}
